import { FC, useEffect, useRef, useState } from "react";
import { StockChip } from "../packing/StockChip";
import { dispatch } from "../../dispatch/dispatch";
import { actions } from "../../dispatch";
import { sizeAr, t } from "../../i18n/ar";
import { IErrorResponse } from "../../interfaces/responses/ErrorResponse.interface";
import { IFindResult } from "../../interfaces/responses/PackingResponse.interface";
import { LocationBadge } from "../packing/LocationBadge";
import { SkuLink } from "../SkuLink";

// ─── "Where is the product?" (أين المنتج؟) ──────────────────────────────────
// The worker scans a product barcode / types a SKU or a few letters of the
// name and gets the slot code in giant letters. Search fires 400 ms after the
// last keystroke, or immediately on Enter (barcode guns send Enter).

interface IFindProps {
    setShowSpinner: (v: boolean) => void;
}

export const Find: FC<IFindProps> = ({ setShowSpinner }) => {
    const [q, setQ] = useState("");
    const [results, setResults] = useState<IFindResult[] | null>(null);
    const [error, setError] = useState("");
    const inputRef = useRef<HTMLInputElement>(null);
    const timer = useRef<number | null>(null);

    useEffect(() => {
        inputRef.current?.focus();
    }, []);

    const search = async (value: string) => {
        const query = value.trim();
        if (query.length < 2) {
            setResults(null);
            return;
        }
        setShowSpinner(true);
        const resp: IFindResult[] | IErrorResponse = await dispatch(actions.findProduct, {}, { q: query });
        setShowSpinner(false);
        if (!resp || "error" in resp) {
            setError(t.findError);
            setResults(null);
            return;
        }
        setError("");
        setResults(resp);
    };

    const onChange = (v: string) => {
        setQ(v);
        if (timer.current) window.clearTimeout(timer.current);
        timer.current = window.setTimeout(() => search(v), 400);
    };

    return (
        <div className="mt-4">
            <h1 className="flex items-center gap-2 text-3xl font-black text-slate-800">
                <span aria-hidden>📍</span> {t.findTitle}
            </h1>
            <p className="mt-1 text-lg font-bold text-slate-500">{t.findHint}</p>

            <form
                className="mt-3"
                onSubmit={(e) => {
                    e.preventDefault();
                    if (timer.current) window.clearTimeout(timer.current);
                    search(q);
                }}
            >
                <input
                    ref={inputRef}
                    value={q}
                    onChange={(e) => onChange(e.target.value)}
                    placeholder={t.findPlaceholder}
                    className="ltr w-full rounded-2xl border-4 border-[#f68b1e] bg-white px-5 py-4 text-center text-3xl font-black text-slate-900 shadow-lg outline-none placeholder:text-slate-300 focus:ring-4 focus:ring-orange-200"
                    autoComplete="off"
                    autoCapitalize="characters"
                />
            </form>

            {error && (
                <p className="mt-4 rounded-2xl bg-red-100 px-4 py-4 text-center text-2xl font-black text-red-700">⚠️ {error}</p>
            )}

            {results && results.length === 0 && (
                <p className="mt-6 rounded-3xl bg-slate-100 px-4 py-8 text-center text-2xl font-black text-slate-500">❌ {t.findEmpty}</p>
            )}

            {results && results.length > 0 && (
                <section className="mt-4 grid gap-4 md:grid-cols-2">
                    {results.map((r) => (
                        <div key={r.groupId} className={`overflow-hidden rounded-3xl border-4 bg-white shadow-lg ${r.exact ? "border-[#f68b1e]" : "border-slate-200"}`}>
                            <div className="flex gap-4 p-4">
                                <img src={r.imageUrl} alt="" className="h-40 w-40 shrink-0 rounded-2xl border border-slate-200 bg-white object-contain" />
                                <div className="flex min-w-0 flex-1 flex-col gap-2">
                                    <LocationBadge location={r.location} overflow={r.overflowLocations} big />
                                    <p className="ltr text-start text-lg font-bold leading-snug text-slate-700">{r.name}</p>
                                    <div className="flex flex-wrap gap-1.5">
                                        {r.skus.slice(0, 4).map((sku) => (
                                            <SkuLink key={sku} sku={sku} url={r.urls?.[sku]} className="ltr inline-block rounded-lg border-2 border-slate-300 bg-slate-50 px-2 py-0.5 font-mono text-base font-black text-slate-900" />
                                        ))}
                                        {r.skus.length > 4 && <span className="ltr rounded-lg bg-slate-100 px-2 py-0.5 text-base font-bold text-slate-500">+{r.skus.length - 4}</span>}
                                    </div>
                                    <div className="mt-auto flex flex-wrap items-center gap-1.5 text-base font-bold">
                                        {r.sizeClass && <span className="rounded-lg bg-slate-100 px-2 py-0.5 text-slate-600">📐 {sizeAr(r.sizeClass)}</span>}
                                        {r.stock != null && <StockChip stock={r.stock} />}
                                    </div>
                                    {r.packingNote && <p className="rounded-xl bg-amber-50 px-3 py-1.5 text-base font-bold text-amber-800">📝 <span className="ltr">{r.packingNote}</span></p>}
                                </div>
                            </div>
                        </div>
                    ))}
                </section>
            )}
        </div>
    );
};
